import { TextSoundGenerator } from "../../util/TextSoundGenerator";

export class DialogueWindow extends Phaser.GameObjects.Container {
    constructor(scene, x, y) {
        super(scene, x, y);
        this.scene = scene;
        this.isTyping = false;
        this.soundGenerator = new TextSoundGenerator(scene);
        this.currentText = '';
        this.skipRequested = false;
        this.isDestroyed = false; // Track if dialogue has been destroyed
        
        // Multi-dialogue state
        this.dialogueQueue = [];
        this.currentDialogueIndex = 0;
        this.isMultiDialogue = false;
        
        // Option dialogue state
        this.isOptionDialogue = false;
        this.optionButtons = {
            accept: null,
            decline: null
        };
        this.optionCallbacks = {
            accept: null,
            decline: null
        };
        this.optionsVisible = false;
        
        if (!scene.textures.exists('dialoguePanel')) {
            scene.load.image('dialoguePanel', '/sprites/ui/Dialogue/DialoguePanel.png');
            scene.load.spritesheet('dialogueDots', 
                '/sprites/ui/Dialogue/dialogueDots.png',
                { 
                    frameWidth: 16, 
                    frameHeight: 16 
                }
            );
            scene.load.once('complete', () => {
                // Safety check: Don't proceed if dialogue was destroyed during loading
                if (this.isDestroyed) return;
                
                this.createDialogueElements();
                this.createDotsAnimation();
                this.setupInteractivity();
            });
            scene.load.start();
        } else {
            this.createDialogueElements();
            this.createDotsAnimation();
            this.setupInteractivity();
        }
        
        scene.add.existing(this);
        
        // Set a very high depth to ensure dialogues always appear on top
        this.setDepth(1000);
        
        // Fix dialogue to screen coordinates (don't move with camera)
        this.setScrollFactor(0);
    }

    // Static helper method to create a dialogue with camera-relative positioning
    static createCameraRelative(scene, offsetX = 10, offsetY = 10) {
        // Since we use setScrollFactor(0), we can position directly in screen coordinates
        const dialogueX = offsetX;
        const dialogueY = offsetY;
        
        return new DialogueWindow(scene, dialogueX, dialogueY);
    }

    setupInteractivity() {
        this.panel.setInteractive();
        
        this.panel.on('pointerdown', () => {
            // Don't handle panel clicks if options are visible (buttons handle their own clicks)
            if (this.optionsVisible) {
                return;
            }
            
            if (this.isTyping) {
                // If currently typing, request a skip
                this.skipRequested = true;
            } else if (this.isComplete) {
                // For option dialogues, options are shown automatically after typing
                // so clicking should advance to next dialogue or do nothing
                if (this.isOptionDialogue) {
                    if (this.isMultiDialogue && this.hasNextDialogue()) {
                        this.advanceToNextDialogue();
                    }
                    // Don't destroy option dialogues on click - only buttons should destroy them
                } else if (this.isMultiDialogue && this.hasNextDialogue()) {
                    this.advanceToNextDialogue();
                } else {
                    // Only auto-destroy non-option dialogues
                    this.destroy();
                }
            }
        });
    }

    preload()  {
        this.load.spritesheet('dots', 
            'sprite/ui/Dialogue/dialogueDonts.png',
            { 
                frameWidth: 16, 
                frameHeight: 16 
            }
        );
    }

    createDotsAnimation() {
        // Safety check: Don't proceed if scene is unavailable or dialogue destroyed
        if (!this.scene || !this.scene.add || this.isDestroyed) return;
        
        // Create the dots animation configuration
        if (!this.scene.anims.exists('dialogue_dots')) {
            this.scene.anims.create({
                key: 'dialogue_dots',
                frames: this.scene.anims.generateFrameNumbers('dialogueDots', {
                    start: 0,
                    end: 6
                }),
                frameRate: 8,
                repeat: -1
            });
        }

        // Create the dots sprite but initially hide it
        this.dots = this.scene.add.sprite(
            this.panel.width - 25,  // Position from right edge
            this.panel.height - 23, // Position from bottom edge
            'dialogueDots'
        );
        this.dots.setOrigin(0.5);
        this.dots.setVisible(false);
        this.dots.setScrollFactor(0); // Ensure dots don't move with camera
        this.add(this.dots);
    }

    createDialogueElements() {
        // Safety check: Don't proceed if scene is unavailable or dialogue destroyed
        if (!this.scene || !this.scene.add || this.isDestroyed) return;
        
        this.panel = this.scene.add.sprite(0, 0, 'dialoguePanel');
        this.panel.setOrigin(0);
        this.panel.setScrollFactor(0); // Ensure panel doesn't move with camera
        this.add(this.panel);
        
        this.dialogueText = this.scene.add.text(63, 10, '', {
            fontSize: '11px',
            color: '#000000',
            wordWrap: { width: 180 }
        });
        this.dialogueText.setScrollFactor(0); // Ensure text doesn't move with camera
        this.add(this.dialogueText);

        // Initialize sound generator
        this.soundGenerator.init();
    }

    async setEmote(emote) {
        // Safety check: Don't proceed if scene is unavailable or dialogue destroyed
        if (!this.scene || !this.scene.add || this.isDestroyed) return;
        
        if (this.emoteSprite) {
            this.emoteSprite.destroy();
        }
        
        const emoteKey = `emote_${emote}`;
        if (!this.scene.textures.exists(emoteKey)) {
            this.scene.load.image(emoteKey, `/sprites/ui/Dialogue/Emotes/${emote}.png`);
            await new Promise(resolve => {
                this.scene.load.once('complete', resolve);
                this.scene.load.start();
            });
        }
        
        // Double-check after async operation
        if (!this.scene || !this.scene.add || this.isDestroyed) return;
        
        const texture = this.scene.textures.get(emoteKey);
        const frame = texture.get();
        
        const speakerFrame = {
            top: 14,
            left: 14,
            right: 45,
            bottom: 45
        };
        
        const centerX = (speakerFrame.left + speakerFrame.right) / 2;
        const centerY = (speakerFrame.top + speakerFrame.bottom) / 2;
        
        const emoteX = centerX - (frame.width / 2) + 3;
        const emoteY = centerY - (frame.height / 2) + 1;
        
        this.emoteSprite = this.scene.add.image(emoteX, emoteY, emoteKey);
        this.emoteSprite.setOrigin(0);
        this.emoteSprite.setScrollFactor(0); // Ensure emote doesn't move with camera
        this.add(this.emoteSprite);
    }

    async typeText(text, speed = 50, customSound = null) {
        // Safety check: Don't proceed if dialogue has been destroyed
        if (this.isDestroyed) return;
        
        if (!this.dialogueText) {
            await new Promise(resolve => {
                const checkText = () => {
                    if (this.dialogueText || this.isDestroyed) {
                        resolve();
                    } else {
                        setTimeout(checkText, 50);
                    }
                };
                checkText();
            });
        }

        // Double-check after waiting
        if (this.isDestroyed) return;

        this.isComplete = false;
        if (this.isTyping) return;
        
        this.isTyping = true;
        this.currentText = text; // Store the full text
        let displayedText = '';
        
        // Play custom sound at the beginning if provided
        if (customSound) {
            try {
                // Check if the sound exists in cache
                if (this.scene.cache.audio.exists(customSound)) {
                    this.scene.sound.play(customSound, { volume: 0.8 });
                } else {
                    console.warn(`Custom dialogue sound '${customSound}' not found in audio cache`);
                }
            } catch (error) {
                console.error(`Error playing custom dialogue sound '${customSound}':`, error);
            }
        }
        
        // Get the text style to calculate line width - use more conservative width
        const style = this.dialogueText.style;
        const maxLineWidth = 170; // More conservative than 180 to account for padding/margins
        
        // Pre-process text to determine word boundaries and wrap points
        const words = text.split(/(\s+)/); // Split on whitespace but keep the whitespace
        
        for (let wordIndex = 0; wordIndex < words.length; wordIndex++) {
            const word = words[wordIndex];
            
            // Skip empty strings
            if (!word) continue;
            
            // Check if skip was requested
            if (this.skipRequested) {
                // Display full text immediately
                if (this.dialogueText && !this.isDestroyed) {
                    this.dialogueText.setText(this.currentText);
                }
                break;
            }
            
            // If it's whitespace, add it directly
            if (/^\s+$/.test(word)) {
                for (const char of word) {
                    if (this.skipRequested || this.isDestroyed) break;
                    
                    displayedText += char;
                    // Safety check before setText
                    if (this.dialogueText && !this.isDestroyed) {
                        this.dialogueText.setText(displayedText);
                    }
                    // Only play character sounds if no custom sound was provided
                    if (!customSound) {
                        this.soundGenerator.playCharacterSound(char);
                    }
                    await new Promise(resolve => setTimeout(resolve, speed));
                }
                continue;
            }
            
            // For actual words, check if we need to wrap before adding
            const lines = displayedText.split('\n');
            const currentLine = lines[lines.length - 1];
            
            // Test if adding this word would exceed the line width
            const testLineWithWord = currentLine + word;
            
            // Safety check: Ensure scene is available
            if (!this.scene || !this.scene.add || this.isDestroyed) {
                break;
            }
            
            const testText = this.scene.add.text(0, 0, testLineWithWord, style);
            const lineWidth = testText.width;
            testText.destroy();
            
            // If the word doesn't fit and we're not at the start of a line, wrap first
            if (lineWidth > maxLineWidth && currentLine.trim().length > 0) {
                displayedText += '\n';
                // Safety check before setText
                if (this.dialogueText && !this.isDestroyed) {
                    this.dialogueText.setText(displayedText);
                }
                // Only play character sounds if no custom sound was provided
                if (!customSound) {
                    this.soundGenerator.playCharacterSound(' ');
                }
                await new Promise(resolve => setTimeout(resolve, speed));
                
                if (this.skipRequested || this.isDestroyed) break;
            }
            
            // Now type the word character by character
            for (const char of word) {
                if (this.skipRequested || this.isDestroyed) break;
                
                displayedText += char;
                // Safety check before setText
                if (this.dialogueText && !this.isDestroyed) {
                    this.dialogueText.setText(displayedText);
                }
                // Only play character sounds if no custom sound was provided
                if (!customSound) {
                    this.soundGenerator.playCharacterSound(char);
                }
                await new Promise(resolve => setTimeout(resolve, speed));
            }
        }
        
        this.isTyping = false;
        this.skipRequested = false; // Reset skip flag
        
        // Safety check before final operations
        if (this.isDestroyed) return;
        
        if (this.dots) {
            this.dots.setVisible(true);
            this.dots.play('dialogue_dots');
            this.isComplete = true;
        }
        
        // For option dialogues, show options immediately after typing completes
        if (this.isOptionDialogue && !this.isMultiDialogue) {
            this.showOptions();
        } else if (this.isOptionDialogue && this.isMultiDialogue && !this.hasNextDialogue()) {
            this.showOptions();
        }
    }

    
    async setDialogue(text, emote, customSound = null) {
        // Reset multi-dialogue state for single dialogue
        this.isMultiDialogue = false;
        this.dialogueQueue = [];
        this.currentDialogueIndex = 0;
        
        // First set the emote immediately
        await this.setEmote(emote);
        
        // Then start typing the text with custom sound if provided
        await this.typeText(text, 50, customSound);
    }

    async setMultiDialogue(dialogueArray) {
        // Validate input
        if (!Array.isArray(dialogueArray) || dialogueArray.length === 0) {
            console.error('setMultiDialogue requires a non-empty array');
            return;
        }

        // Validate each dialogue entry - now supports 2 or 3 elements
        for (let i = 0; i < dialogueArray.length; i++) {
            const dialogue = dialogueArray[i];
            if (!Array.isArray(dialogue) || dialogue.length < 2 || dialogue.length > 3) {
                console.error(`Invalid dialogue format at index ${i}. Expected [text, emote] or [text, emote, customSound]`);
                return;
            }
        }

        // Set up multi-dialogue state
        this.isMultiDialogue = true;
        this.dialogueQueue = dialogueArray;
        this.currentDialogueIndex = 0;

        // Start with the first dialogue
        await this.showCurrentDialogue();
    }

    async showCurrentDialogue() {
        if (this.currentDialogueIndex >= this.dialogueQueue.length) {
            return;
        }

        const dialogueEntry = this.dialogueQueue[this.currentDialogueIndex];
        const text = dialogueEntry[0];
        const emote = dialogueEntry[1];
        const customSound = dialogueEntry.length > 2 ? dialogueEntry[2] : null;
        
        // Hide dots from previous dialogue
        if (this.dots) {
            this.dots.setVisible(false);
        }

        // Hide options during dialogue advancement
        if (this.optionsVisible) {
            this.hideOptions();
        }

        // Set emote and display text
        await this.setEmote(emote);
        
        // Use faster typing speed for option dialogues, and pass custom sound
        if (this.isOptionDialogue) {
            await this.typeText(text, 50, customSound);
        } else {
            await this.typeText(text, 50, customSound);
        }
    }

    hasNextDialogue() {
        return this.currentDialogueIndex < this.dialogueQueue.length - 1;
    }

    async advanceToNextDialogue() {
        if (!this.hasNextDialogue()) {
            return false;
        }

        this.currentDialogueIndex++;
        await this.showCurrentDialogue();
        return true;
    }

    // New Option Dialogue Methods
    async setOptionDialogue(text, emote, acceptCallback, declineCallback, customSound = null) {
        // Reset all dialogue states
        this.isMultiDialogue = false;
        this.isOptionDialogue = true;
        this.dialogueQueue = [];
        this.currentDialogueIndex = 0;
        this.optionsVisible = false;
        
        // Store callbacks
        this.optionCallbacks.accept = acceptCallback;
        this.optionCallbacks.decline = declineCallback;
        
        // Create option buttons
        this.createOptionButtons();
        
        // First set the emote immediately
        await this.setEmote(emote);
        
        // Then start typing the text (faster for option dialogues) with custom sound
        await this.typeText(text, 50, customSound);
    }

    async setMultiOptionDialogue(dialogueArray, acceptCallback, declineCallback) {
        // Validate input
        if (!Array.isArray(dialogueArray) || dialogueArray.length === 0) {
            return;
        }

        // Validate each dialogue entry - now supports 2 or 3 elements
        for (let i = 0; i < dialogueArray.length; i++) {
            const dialogue = dialogueArray[i];
            if (!Array.isArray(dialogue) || dialogue.length < 2 || dialogue.length > 3) {
                console.error(`Invalid dialogue format at index ${i}. Expected [text, emote] or [text, emote, customSound]`);
                return;
            }
        }

        // Set up option dialogue state
        this.isMultiDialogue = true;
        this.isOptionDialogue = true;
        this.dialogueQueue = dialogueArray;
        this.currentDialogueIndex = 0;
        this.optionsVisible = false;

        // Store callbacks
        this.optionCallbacks.accept = acceptCallback;
        this.optionCallbacks.decline = declineCallback;
        
        // Create option buttons
        this.createOptionButtons();

        // Start with the first dialogue
        await this.showCurrentDialogue();
    }

    createOptionButtons() {
        // Safety check: Don't proceed if scene is unavailable or dialogue destroyed
        if (!this.scene || !this.scene.add || this.isDestroyed) return;
        
        // Calculate button positions (bottom area of dialogue panel)
        const buttonY = this.panel.height + 10;
        const acceptX = 80;
        const declineX = 165;

        // Create Accept button
        this.optionButtons.accept = this.scene.add.image(acceptX, buttonY, 'dialogue-option-accept');
        this.optionButtons.accept.setOrigin(0.5);
        this.optionButtons.accept.setScrollFactor(0);
        this.optionButtons.accept.setInteractive();
        this.optionButtons.accept.setVisible(false);
        this.add(this.optionButtons.accept);

        // Create Decline button
        this.optionButtons.decline = this.scene.add.image(declineX, buttonY, 'dialogue-option-decline');
        this.optionButtons.decline.setOrigin(0.5);
        this.optionButtons.decline.setScrollFactor(0);
        this.optionButtons.decline.setInteractive();
        this.optionButtons.decline.setVisible(false);
        this.add(this.optionButtons.decline);

        // Setup Accept button events
        this.optionButtons.accept.on('pointerover', () => {
            this.optionButtons.accept.setTexture('dialogue-option-accept-hovered');
        });
        
        this.optionButtons.accept.on('pointerout', () => {
            this.optionButtons.accept.setTexture('dialogue-option-accept');
        });
        
        this.optionButtons.accept.on('pointerdown', () => {
            this.optionButtons.accept.setTexture('dialogue-option-accept-selected');
        });
        
        this.optionButtons.accept.on('pointerup', () => {
            this.optionButtons.accept.setTexture('dialogue-option-accept-hovered');
            // Execute accept callback and destroy dialogue
            if (this.optionCallbacks.accept) {
                this.optionCallbacks.accept();
            }
            this.destroy();
        });

        // Setup Decline button events
        this.optionButtons.decline.on('pointerover', () => {
            this.optionButtons.decline.setTexture('dialogue-option-decline-hovered');
        });
        
        this.optionButtons.decline.on('pointerout', () => {
            this.optionButtons.decline.setTexture('dialogue-option-decline');
        });
        
        this.optionButtons.decline.on('pointerdown', () => {
            this.optionButtons.decline.setTexture('dialogue-option-decline-selected');
        });
        
        this.optionButtons.decline.on('pointerup', () => {
            this.optionButtons.decline.setTexture('dialogue-option-decline-hovered');
            // Execute decline callback and destroy dialogue
            if (this.optionCallbacks.decline) {
                this.optionCallbacks.decline();
            }
            this.destroy();
        });
    }

    showOptions() {
        // Safety check: Don't proceed if dialogue has been destroyed
        if (this.isDestroyed) return;
        
        if (this.optionButtons.accept && this.optionButtons.decline) {
            this.optionButtons.accept.setVisible(true);
            this.optionButtons.decline.setVisible(true);
            this.optionsVisible = true;
            
            // Hide dots when options are shown
            if (this.dots) {
                this.dots.setVisible(false);
            }
        }
    }

    hideOptions() {
        if (this.optionButtons.accept && this.optionButtons.decline) {
            this.optionButtons.accept.setVisible(false);
            this.optionButtons.decline.setVisible(false);
            this.optionsVisible = false;
        }
    }

    destroy() {
        // Set destroyed flag to prevent further operations
        this.isDestroyed = true;
        
        if (this.soundGenerator) {
            this.soundGenerator.destroy();
        }
        
        if (this.emoteSprite) {
            this.emoteSprite.destroy();
        }

        if (this.dialogueText) {
            this.dialogueText.destroy();
        }

        // Clean up option buttons
        if (this.optionButtons.accept) {
            this.optionButtons.accept.destroy();
        }
        if (this.optionButtons.decline) {
            this.optionButtons.decline.destroy();
        }

        if (this.panel) {
            this.panel.destroy();
        }

        super.destroy();
    }
}